// =============================================================================
// Project Management module — owners / EPCs list
//
// One ModelListPage for both registries; they share the ProjectManagementParty shape. The logo column
// shows what the auto-lookup found (or nothing, when it missed) so a bad or missing mark is visible
// at a glance rather than one project at a time.
// =============================================================================

import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { ModelListPage, ModelListPageStyled } from '/imports/ui/ModelListPage';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { useUser, useFindOne } from '/imports/hooks';
import { Accounts } from '/imports/api/accounts';
import { fileHref } from '/imports/api/files';
import { ModelClass } from '/imports/types';
import {
  ProjectManagementParty, projectManagementEpcModelClass, projectManagementIsAdmin,
  projectManagementOwnerModelClass, projectManagementUtilityModelClass,
} from '/imports/api/projectManagement';

const PartiesListStyled = styled(ModelListPageStyled)`
  /* Capped so a wide wordmark cannot set the row height; contain keeps the aspect ratio. */
  .party-logo { display: block; height: 1.6rem; max-width: 9rem; object-fit: contain; object-position: left; }
  .no-logo { color: ${css.grey5}; }
`;

/** Catalog of one party registry. */
const PartiesPage = ({ modelClass, pathPrefix }: { modelClass: ModelClass<ProjectManagementParty, any>, pathPrefix: string }) => {
  const user = useUser();
  const account = useFindOne(() => Accounts.find({ orgId: user?.orgId, userId: user?._id }), [user?._id]);
  // Org-wide reference data, like portfolios — admin-only.
  if (!projectManagementIsAdmin(user, account)) return <NotPermittedPage />;
  return (
    <ModelListPage
      modelClass={modelClass}
      styled={PartiesListStyled}
      defaultSort={{ name: 1 }}
      columns={[
        { field: 'name', render: (doc) => <Link to={`${pathPrefix}/${doc._id}`}>{doc.name}</Link> },
        {
          field: 'logo',
          label: strings.Logo,
          render: (doc) => (doc.logo
            ? <img className="party-logo" src={fileHref(doc.logo)} alt={doc.name} />
            : <span className="no-logo">—</span>),
        },
        { field: 'website', label: strings.Website },
        { field: 'note', label: strings.Note },
      ]}
    />
  );
};

export const ProjectManagementOwnersPage = () => <PartiesPage modelClass={projectManagementOwnerModelClass} pathPrefix="/project-management/owners" />;
export const ProjectManagementEpcsPage = () => <PartiesPage modelClass={projectManagementEpcModelClass} pathPrefix="/project-management/epcs" />;
export const ProjectManagementUtilitiesPage = () => <PartiesPage modelClass={projectManagementUtilityModelClass} pathPrefix="/project-management/utilities" />;
